import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  ChevronRight,
  ChevronLeft,
  Check,
  Brain,
  Heart,
  Crown,
  Zap,
  Search,
  Clock,
  Gift,
  Award,
  Lock,
  Plus,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Archetype icons mapping
const ARCHETYPE_ICONS = {
  comfort: Heart,
  status: Crown,
  impulse: Zap,
  rationalizer: Search,
  escape: Clock,
  hunter: Gift
};

// Quiz answer to archetype mapping (A→comfort, B→status, C→impulse, D→rationalizer, E→escape, F→hunter)
const QUIZ_ANSWER_MAP = ['comfort', 'status', 'impulse', 'rationalizer', 'escape', 'hunter'];

// Tiebreaker priority order for secondary archetype selection (Bug 3 fix)
const ARCHETYPE_PRIORITY = ['comfort', 'status', 'impulse', 'rationalizer', 'escape', 'hunter'];

// Strategy combinations based on dominant + secondary behaviors (order-independent)
const STRATEGY_COMBINATIONS = {
  'comfort_rationalizer': [1, 4, 6],
  'comfort_impulse': [1, 2, 6],
  'comfort_escape': [1, 4, 6],
  'comfort_status': [1, 5, 6],
  'comfort_hunter': [1, 2, 6],
  'status_rationalizer': [3, 5, 6],
  'status_impulse': [2, 3, 6],
  'status_hunter': [2, 5, 6],
  'status_escape': [3, 4, 6],
  'impulse_escape': [2, 3, 6],
  'impulse_rationalizer': [1, 2, 6],
  'impulse_hunter': [2, 3, 6],
  'rationalizer_hunter': [1, 5, 6],
  'rationalizer_escape': [1, 4, 6],
  'escape_hunter': [2, 4, 6],
  // Reverse combinations (order-independent lookup)
  'rationalizer_comfort': [1, 4, 6],
  'impulse_comfort': [1, 2, 6],
  'escape_comfort': [1, 4, 6],
  'status_comfort': [1, 5, 6],
  'hunter_comfort': [1, 2, 6],
  'rationalizer_status': [3, 5, 6],
  'impulse_status': [2, 3, 6],
  'hunter_status': [2, 5, 6],
  'escape_status': [3, 4, 6],
  'escape_impulse': [2, 3, 6],
  'rationalizer_impulse': [1, 2, 6],
  'hunter_impulse': [2, 3, 6],
  'hunter_rationalizer': [1, 5, 6],
  'escape_rationalizer': [1, 4, 6],
  'hunter_escape': [2, 4, 6]
};

// Fallback strategies when secondary archetype has 0 points (dominant-only)
const DOMINANT_ONLY_STRATEGIES = {
  comfort: [1, 4, 6],
  status: [3, 5, 6],
  impulse: [2, 3, 6],
  rationalizer: [1, 5, 6],
  escape: [1, 4, 6],
  hunter: [2, 5, 6]
};

// Animation variants
const pageVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 }
};

// Start Button Component (exported for dashboard)
export const UnderstandingExpensesStartButton = ({ onClick }) => {
  const { t } = useTranslation();
  
  return (
    <button
      onClick={onClick}
      data-testid="understanding-expenses-start-btn"
      className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-medium rounded-xl transition-all flex items-center justify-center gap-2"
    >
      <Brain className="w-5 h-5" />
      {t('understandingExpenses.title')}
    </button>
  );
};

const UnderstandingExpensesModule = ({ onClose, onComplete, isPremium = false }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  
  // Screen and progress state
  const [currentScreen, setCurrentScreen] = useState(1);
  const [hasExistingProgress, setHasExistingProgress] = useState(false);
  const [showContinuePrompt, setShowContinuePrompt] = useState(false);
  
  // Quiz and archetype state
  const [quizAnswers, setQuizAnswers] = useState(Array(6).fill(null));
  const [archetype, setArchetype] = useState(null);
  const [secondaryArchetype, setSecondaryArchetype] = useState(null);
  
  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddingTasks, setIsAddingTasks] = useState(false);
  const [tasksAdded, setTasksAdded] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false); // Premium paywall state
  
  // Total screens
  const TOTAL_SCREENS = 30;
  
  // Load existing progress
  useEffect(() => {
    const loadProgress = async () => {
      try {
        const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.get(`${API}/api/understanding-expenses/progress`, { headers });
        
        if (response.data && !response.data.completed) {
          setHasExistingProgress(true);
          setShowContinuePrompt(true);
          if (response.data.archetype) setArchetype(response.data.archetype);
          if (response.data.secondary_archetype) setSecondaryArchetype(response.data.secondary_archetype);
          if (response.data.current_screen) {
            const savedScreen = response.data.current_screen;
            // Fix 2: If user is Free and saved progress is in Premium territory, cap at screen 11
            if (!isPremium && savedScreen >= 12) {
              setCurrentScreen(11);
            } else {
              setCurrentScreen(savedScreen);
            }
          }
          if (response.data.answers && Array.isArray(response.data.answers)) {
            setQuizAnswers(response.data.answers);
          }
        }
      } catch (error) {
        console.log('No existing progress:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadProgress();
  }, [isPremium]);
  
  // Save progress
  const saveProgress = useCallback(async (completed = false) => {
    try {
      setIsSaving(true);
      const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/api/understanding-expenses/progress`, {
        current_screen: currentScreen,
        archetype,
        secondary_archetype: secondaryArchetype,
        answers: quizAnswers,
        completed
      }, { headers });
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setIsSaving(false);
    }
  }, [currentScreen, archetype, secondaryArchetype, quizAnswers]);
  
  // Calculate archetypes from quiz answers (Bug 3 fix: deterministic tie-breaking)
  const calculateArchetypes = useCallback(() => {
    const scores = {
      comfort: 0,
      status: 0,
      impulse: 0,
      rationalizer: 0,
      escape: 0,
      hunter: 0
    };
    
    quizAnswers.forEach((answerIndex) => {
      if (answerIndex !== null && answerIndex >= 0 && answerIndex < 6) {
        const archetypeKey = QUIZ_ANSWER_MAP[answerIndex];
        if (archetypeKey) scores[archetypeKey]++;
      }
    });
    
    // Sort by score descending, then by priority order for tie-breaking (Bug 3 fix)
    const sorted = Object.entries(scores).sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1]; // Higher score first
      // Same score: use priority order (comfort > status > impulse > rationalizer > escape > hunter)
      return ARCHETYPE_PRIORITY.indexOf(a[0]) - ARCHETYPE_PRIORITY.indexOf(b[0]);
    });
    
    const dominant = sorted[0][0];
    // Secondary is only set if it has > 0 points (Bug 1 fix: proper fallback handling)
    const secondary = sorted[1][1] > 0 ? sorted[1][0] : null;
    
    setArchetype(dominant);
    setSecondaryArchetype(secondary);
    
    return { dominant, secondary };
  }, [quizAnswers]);
  
  // Get recommended strategies for the combination (Bug 1 fix: use combination matrix with fallback)
  const getRecommendedStrategies = () => {
    if (!archetype) return [1, 4, 6]; // Absolute fallback
    
    // If no secondary archetype (all answers same archetype), use dominant-only strategies
    if (!secondaryArchetype) {
      return DOMINANT_ONLY_STRATEGIES[archetype] || [1, 4, 6];
    }
    
    // Try both key combinations (order-independent)
    const key1 = `${archetype}_${secondaryArchetype}`;
    const key2 = `${secondaryArchetype}_${archetype}`;
    
    return STRATEGY_COMBINATIONS[key1] || STRATEGY_COMBINATIONS[key2] || DOMINANT_ONLY_STRATEGIES[archetype] || [1, 4, 6];
  };
  
  // Navigation (Bug 4 fix: save progress on every screen transition)
  const goNext = () => {
    const nextScreen = currentScreen + 1;
    
    // Fix 1: Block Free users from accessing Premium screens (12+)
    // Show paywall when Free user tries to go past screen 10 (the result screen)
    if (nextScreen >= 12 && !isPremium) {
      setShowPaywall(true);
      return;
    }
    
    // Premium users skip screen 11 (paywall screen) - go directly to screen 12
    if (nextScreen === 11 && isPremium) {
      setCurrentScreen(12);
      saveProgress(false);
      return;
    }
    
    setCurrentScreen(nextScreen);
    
    // Save progress on every screen transition (Bug 4 fix)
    saveProgress(nextScreen === 30);
  };
  
  const goBack = () => {
    if (currentScreen > 1) {
      setCurrentScreen(currentScreen - 1);
    }
  };
  
  const handleContinue = () => {
    setShowContinuePrompt(false);
  };
  
  const handleStartFresh = () => {
    setCurrentScreen(1);
    setQuizAnswers(Array(6).fill(null));
    setArchetype(null);
    setSecondaryArchetype(null);
    setShowContinuePrompt(false);
  };
  
  const handleQuizAnswer = (answerIndex) => {
    const currentQuestionIndex = currentScreen - 3; // Screens 3-8 are questions 0-5
    const newAnswers = [...quizAnswers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setQuizAnswers(newAnswers);
    
    // Auto-advance after answering
    if (currentScreen < 8) {
      setTimeout(() => goNext(), 300);
    } else {
      // Last question - calculate and go to analyzing screen
      setTimeout(() => {
        calculateArchetypes();
        goNext();
      }, 300);
    }
  };
  
  // Add tasks for personalized action plan (Bug 2 fix: always create exactly 3 tasks)
  const handleAddTasks = async () => {
    if (!archetype) return;
    
    setIsAddingTasks(true);
    try {
      const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const strategies = getRecommendedStrategies();
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 7);
      
      let tasksCreated = 0;
      
      // Create exactly 3 tasks - one per selected strategy (Bug 2 fix)
      for (let i = 0; i < strategies.length; i++) {
        const strategyNum = strategies[i];
        const strategyKey = `strategy${strategyNum}`;
        const strategyName = t(`understandingExpenses.strategies.${strategyKey}.name`);
        const strategyHow = t(`understandingExpenses.strategies.${strategyKey}.how`);
        
        // Priority: first strategy is high, rest are medium (Bug 2 fix)
        const priority = i === 0 ? 'high' : 'medium';
        
        try {
          await axios.post(`${API}/api/tasks`, {
            title: strategyName,
            description: strategyHow,
            category: 'financial', // Bug 2 fix: correct category
            due_date: dueDate.toISOString(),
            priority: priority,
            source: 'understanding_expenses', // Bug 2 fix: add source field
            estimated_impact: 0
          }, { headers });
          
          tasksCreated++;
          // Stagger due dates by 7 days
          dueDate.setDate(dueDate.getDate() + 7);
        } catch (taskError) {
          // Log error but continue creating remaining tasks (Bug 2 fix: don't block flow)
          console.error(`Error creating task for strategy ${strategyNum}:`, taskError);
        }
      }
      
      if (tasksCreated > 0) {
        setTasksAdded(true);
        await saveProgress(true);
        
        // Dispatch completion event
        window.dispatchEvent(new CustomEvent('moduleCompleted', {
          detail: { module: 'understanding_expenses', archetype, secondaryArchetype }
        }));
        
        setTimeout(() => {
          if (onComplete) onComplete({ archetype, secondaryArchetype });
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error adding tasks:', error);
    } finally {
      setIsAddingTasks(false);
    }
  };
  
  const handleSkip = async () => {
    await saveProgress(true);
    window.dispatchEvent(new CustomEvent('moduleCompleted', {
      detail: { module: 'understanding_expenses', archetype, secondaryArchetype }
    }));
    if (onComplete) onComplete({ archetype, secondaryArchetype });
    onClose();
  };
  
  const handleUpgrade = () => {
    saveProgress();
    window.dispatchEvent(new CustomEvent('openPremiumModal'));
  };

  // ============================================================================
  // RENDER COMPONENTS
  // ============================================================================
  
  // Navigation buttons component (follows content with mt-8)
  const NavigationButtons = ({ showBack = true, nextLabel, onNext, disabled = false }) => (
    <div className="flex justify-between items-center mt-8 pt-4">
      {showBack && currentScreen > 1 ? (
        <button
          onClick={goBack}
          className="px-4 py-2 text-gray-400 hover:text-white transition-all flex items-center gap-1"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          {t('understandingExpenses.back')}
        </button>
      ) : <div />}
      <button
        onClick={onNext || goNext}
        disabled={disabled}
        className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-lg hover:from-purple-500 hover:to-indigo-500 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="next-btn"
      >
        {nextLabel || t('understandingExpenses.next')}
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );

  // Continue prompt
  const renderContinuePrompt = () => (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center"
    >
      <Brain className="w-16 h-16 text-purple-400 mb-6" />
      <h2 className="text-2xl font-bold text-white mb-4">
        {t('understandingExpenses.title')}
      </h2>
      <p className="text-gray-300 mb-8">
        {lang === 'es' ? 'Tienes progreso guardado.' : 'You have saved progress.'}
      </p>
      <div className="flex flex-col gap-3 w-full max-w-xs">
        <button
          onClick={handleContinue}
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-lg hover:from-purple-500 hover:to-indigo-500 transition-all"
          data-testid="continue-btn"
        >
          {t('understandingExpenses.continueButton')}
        </button>
        <button
          onClick={handleStartFresh}
          className="px-6 py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-all"
          data-testid="start-fresh-btn"
        >
          {t('understandingExpenses.startButton')}
        </button>
      </div>
    </motion.div>
  );

  // Screen 1 - Welcome
  const renderScreen1 = () => {
    const content = t('understandingExpenses.screen1', { returnObjects: true });
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="px-4 md:px-8 py-6">
        <div className="max-w-lg mx-auto text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">{content.headline}</h1>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">{content.body}</p>
          <p className="text-gray-400 text-sm mb-4">{content.subtext}</p>
          <p className="text-purple-400 text-sm font-medium">{content.duration}</p>
          <NavigationButtons showBack={false} />
        </div>
      </motion.div>
    );
  };

  // Screen 2 - Before the questions
  const renderScreen2 = () => {
    const content = t('understandingExpenses.screen2', { returnObjects: true });
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="px-4 md:px-8 py-6">
        <div className="max-w-lg mx-auto text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">{content.headline}</h1>
          <p className="text-gray-300 text-base leading-relaxed mb-4">{content.body}</p>
          <p className="text-gray-300 text-base leading-relaxed mb-6">{content.body2}</p>
          <p className="text-purple-400 font-medium mb-6">{content.cta}</p>
          <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-xl p-4">
            <p className="text-yellow-300 text-sm italic">{content.disclaimer}</p>
          </div>
          <NavigationButtons />
        </div>
      </motion.div>
    );
  };

  // Screens 3-8 - Quiz questions
  const renderQuizQuestion = () => {
    const questionIndex = currentScreen - 3; // 0-5
    const questions = t('understandingExpenses.quizQuestions', { returnObjects: true });
    const question = questions[questionIndex];
    
    if (!question) return null;
    
    return (
      <motion.div
        key={`quiz-${questionIndex}`}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="px-4 md:px-8 py-6"
      >
        <div className="max-w-lg mx-auto">
          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-400 mb-2">
              <span>{lang === 'es' ? 'Pregunta' : 'Question'} {questionIndex + 1} / 6</span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full">
              <div 
                className="h-full bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full transition-all"
                style={{ width: `${((questionIndex + 1) / 6) * 100}%` }}
              />
            </div>
          </div>
          
          <h2 className="text-lg md:text-xl font-bold text-white mb-6 text-center">
            {question.question}
          </h2>
          
          <div className="space-y-3">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleQuizAnswer(index)}
                className={`w-full px-4 py-3 text-left rounded-lg border transition-all ${
                  quizAnswers[questionIndex] === index
                    ? 'bg-purple-900/50 border-purple-500 text-white'
                    : 'bg-gray-800 border-gray-700 hover:border-purple-500 text-gray-300'
                }`}
                data-testid={`quiz-option-${index}`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </motion.div>
    );
  };

  // Screen 9 - Analyzing (auto-advance effect handled at component level)
  useEffect(() => {
    if (currentScreen === 9) {
      const timer = setTimeout(() => {
        if (!archetype) calculateArchetypes();
        goNext();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentScreen, archetype, calculateArchetypes]);
  
  const renderScreen9 = () => {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="px-4 md:px-8 py-6">
        <div className="max-w-lg mx-auto text-center min-h-[60vh] flex flex-col items-center justify-center">
          <Loader2 className="w-12 h-12 text-purple-400 animate-spin mb-6" />
          <p className="text-gray-300 text-lg">{t('understandingExpenses.screen9.analyzing')}</p>
        </div>
      </motion.div>
    );
  };

  // Screen 10 - Results (shows both dominant and secondary)
  const renderScreen10 = () => {
    if (!archetype) {
      calculateArchetypes();
      return null;
    }
    
    const content = t('understandingExpenses.screen10', { returnObjects: true });
    const dominantData = t(`understandingExpenses.archetypes.${archetype}`, { returnObjects: true });
    const secondaryData = secondaryArchetype ? t(`understandingExpenses.archetypes.${secondaryArchetype}`, { returnObjects: true }) : null;
    const DominantIcon = ARCHETYPE_ICONS[archetype];
    const SecondaryIcon = secondaryArchetype ? ARCHETYPE_ICONS[secondaryArchetype] : null;
    
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="px-4 md:px-8 py-6">
        <div className="max-w-lg mx-auto">
          {/* Combination note */}
          <p className="text-gray-400 text-sm text-center mb-6">{content.combination_note}</p>
          
          {/* Dominant behavior */}
          <div className="mb-6">
            <p className="text-purple-400 text-sm font-medium mb-2">{content.dominant_label}</p>
            <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-purple-600/20 flex items-center justify-center">
                  <DominantIcon className="w-6 h-6 text-purple-400" />
                </div>
                <h2 className="text-xl font-bold text-white">{dominantData.name}</h2>
              </div>
              <p className="text-gray-400 text-sm mb-2">{dominantData.trigger}</p>
              <p className="text-gray-300 text-sm mb-3">{dominantData.description}</p>
              <p className="text-purple-300 text-xs italic">{dominantData.data}</p>
            </div>
          </div>
          
          {/* Secondary behavior */}
          {secondaryArchetype && secondaryData && (
            <div className="mb-6">
              <p className="text-indigo-400 text-sm font-medium mb-2">{content.secondary_label}</p>
              <div className="bg-indigo-900/30 border border-indigo-500/30 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-600/20 flex items-center justify-center">
                    <SecondaryIcon className="w-5 h-5 text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{secondaryData.name}</h3>
                </div>
                <p className="text-gray-400 text-sm mb-2">{secondaryData.trigger}</p>
                <p className="text-gray-300 text-sm">{secondaryData.description}</p>
              </div>
            </div>
          )}
          
          <NavigationButtons />
        </div>
      </motion.div>
    );
  };

  // Render paywall screen for Free users (reuses screen11 content)
  const renderPaywall = () => {
    const content = t('understandingExpenses.screen11', { returnObjects: true });
    
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="px-4 md:px-8 py-6">
        <div className="max-w-lg mx-auto text-center">
          <h1 className="text-2xl font-bold text-white mb-4">{content.headline}</h1>
          <p className="text-gray-300 mb-6">{content.body}</p>
          
          <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4 mb-6 text-left">
            <p className="text-yellow-400 font-medium mb-3">{content.premium_label}</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-gray-300 text-sm">
                <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span>{content.item1}</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300 text-sm">
                <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span>{content.item2}</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300 text-sm">
                <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span>{content.item3}</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300 text-sm">
                <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span>{content.item4}</span>
              </li>
            </ul>
          </div>
          
          <p className="text-gray-500 text-sm mb-6">{content.free_note}</p>
          
          <div className="space-y-3">
            <button
              onClick={handleUpgrade}
              className="w-full px-6 py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-lg hover:from-yellow-400 hover:to-amber-500 transition-all flex items-center justify-center gap-2"
              data-testid="upgrade-btn"
            >
              <Lock className="w-5 h-5" />
              {content.cta_button}
            </button>
            <button
              onClick={handleSkip}
              className="w-full px-4 py-2 text-gray-400 hover:text-white transition-all"
              data-testid="skip-btn"
            >
              {content.skip}
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  // Generic content screen for premium screens 12-28
  const renderContentScreen = (screenNum) => {
    const content = t(`understandingExpenses.screen${screenNum}`, { returnObjects: true });
    if (!content || typeof content !== 'object') return null;
    
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="px-4 md:px-8 py-6">
        <div className="max-w-lg mx-auto">
          {content.headline && (
            <h1 className="text-xl md:text-2xl font-bold text-white mb-4 text-center">{content.headline}</h1>
          )}
          {content.intro && (
            <p className="text-gray-300 text-center mb-6">{content.intro}</p>
          )}
          {content.body && (
            <p className="text-gray-300 leading-relaxed mb-4">{content.body}</p>
          )}
          {content.body1 && (
            <p className="text-gray-300 leading-relaxed mb-4">{content.body1}</p>
          )}
          {content.body2 && (
            <p className="text-gray-300 leading-relaxed mb-4">{content.body2}</p>
          )}
          {content.subtext && (
            <p className="text-gray-400 text-sm mb-6">{content.subtext}</p>
          )}
          
          {/* Factor boxes for screen 13 */}
          {content.factor1_title && (
            <div className="space-y-4 mb-6">
              <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-4">
                <h3 className="text-purple-300 font-bold mb-2">{content.factor1_title}</h3>
                <p className="text-gray-300 text-sm">{content.factor1}</p>
              </div>
              <div className="bg-indigo-900/30 border border-indigo-500/30 rounded-xl p-4">
                <h3 className="text-indigo-300 font-bold mb-2">{content.factor2_title}</h3>
                <p className="text-gray-300 text-sm">{content.factor2}</p>
              </div>
            </div>
          )}
          
          {content.closing && (
            <p className="text-gray-300 italic text-center">{content.closing}</p>
          )}
          
          <NavigationButtons />
        </div>
      </motion.div>
    );
  };

  // Screens 15-20 - Deep dive per behavior
  const renderBehaviorDeepDive = (behaviorKey) => {
    const content = t(`understandingExpenses.behaviors.${behaviorKey}`, { returnObjects: true });
    if (!content || typeof content !== 'object') return renderContentScreen(currentScreen);
    
    const Icon = ARCHETYPE_ICONS[behaviorKey];
    
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="px-4 md:px-8 py-6">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-purple-600/20 flex items-center justify-center">
              <Icon className="w-6 h-6 text-purple-400" />
            </div>
            <h1 className="text-xl font-bold text-white">{content.headline}</h1>
          </div>
          
          <div className="space-y-4">
            <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
              <p className="text-purple-400 text-sm font-medium mb-2">{content.origin_label}</p>
              <p className="text-gray-300 text-sm">{content.origin}</p>
            </div>
            
            <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
              <p className="text-indigo-400 text-sm font-medium mb-2">{content.progression_label}</p>
              <p className="text-gray-300 text-sm">{content.progression}</p>
            </div>
            
            <div className="bg-red-900/30 border border-red-500/30 rounded-xl p-4">
              <p className="text-red-400 text-sm font-medium mb-2">{content.consequence_label}</p>
              <p className="text-gray-300 text-sm">{content.consequence}</p>
            </div>
            
            <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-xl p-4">
              <p className="text-yellow-400 text-sm font-medium mb-2">{content.combination_label}</p>
              <p className="text-gray-300 text-sm">{content.combination}</p>
            </div>
          </div>
          
          <NavigationButtons />
        </div>
      </motion.div>
    );
  };

  // Screen 21 - Your specific combination
  const renderScreen21 = () => {
    const content = t('understandingExpenses.screen21', { returnObjects: true });
    const dominantName = t(`understandingExpenses.archetypes.${archetype}.name`);
    const secondaryName = secondaryArchetype ? t(`understandingExpenses.archetypes.${secondaryArchetype}.name`) : '';
    
    // Generate dynamic combination description
    const dominantCombo = t(`understandingExpenses.behaviors.${archetype}.combination`, { returnObjects: true });
    
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="px-4 md:px-8 py-6">
        <div className="max-w-lg mx-auto text-center">
          <h1 className="text-xl md:text-2xl font-bold text-white mb-4">
            {content.headline.replace('[DOMINANT]', dominantName).replace('[SECONDARY]', secondaryName)
              .replace('[DOMINANTE]', dominantName).replace('[SECUNDARIO]', secondaryName)}
          </h1>
          <p className="text-gray-300 mb-6">{content.intro}</p>
          
          <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-4">
            <p className="text-gray-300 text-sm">{dominantCombo}</p>
          </div>
          
          <NavigationButtons />
        </div>
      </motion.div>
    );
  };

  // Screens 23-28 - Strategies (one per screen)
  const renderStrategyScreen = (strategyNum) => {
    const content = t(`understandingExpenses.strategies.strategy${strategyNum}`, { returnObjects: true });
    if (!content || typeof content !== 'object') return renderContentScreen(currentScreen);
    
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="px-4 md:px-8 py-6">
        <div className="max-w-lg mx-auto">
          <p className="text-purple-400 text-sm font-medium mb-2">{content.strategy_number}</p>
          <h1 className="text-xl md:text-2xl font-bold text-white mb-6">{content.name}</h1>
          
          <div className="space-y-4">
            <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
              <p className="text-purple-400 text-sm font-medium mb-2">{content.what_label}</p>
              <p className="text-gray-300 text-sm">{content.what}</p>
            </div>
            
            <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
              <p className="text-indigo-400 text-sm font-medium mb-2">{content.why_label}</p>
              <p className="text-gray-300 text-sm">{content.why}</p>
            </div>
            
            <div className="bg-green-900/30 border border-green-500/30 rounded-xl p-4">
              <p className="text-green-400 text-sm font-medium mb-2">{content.how_label}</p>
              <p className="text-gray-300 text-sm">{content.how}</p>
            </div>
            
            <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-xl p-4">
              <p className="text-yellow-400 text-sm font-medium mb-2">{content.effective_label}</p>
              <p className="text-gray-300 text-sm">{content.effective}</p>
            </div>
            
            {content.bridge && (
              <p className="text-gray-400 text-sm italic text-center mt-4">{content.bridge}</p>
            )}
          </div>
          
          <NavigationButtons />
        </div>
      </motion.div>
    );
  };

  // Screen 29 - Personalized action plan
  const renderScreen29 = () => {
    const content = t('understandingExpenses.screen29', { returnObjects: true });
    const strategies = getRecommendedStrategies();
    
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="px-4 md:px-8 py-6">
        <div className="max-w-lg mx-auto">
          <h1 className="text-xl md:text-2xl font-bold text-white mb-4 text-center">{content.headline}</h1>
          <p className="text-gray-300 text-center mb-6">{content.intro}</p>
          
          <div className="space-y-4 mb-6">
            {strategies.map((strategyNum, index) => {
              const strategyContent = t(`understandingExpenses.strategies.strategy${strategyNum}`, { returnObjects: true });
              return (
                <div key={strategyNum} className={`rounded-xl p-4 ${
                  index === 0 ? 'bg-purple-900/30 border border-purple-500/30' :
                  index === 1 ? 'bg-indigo-900/30 border border-indigo-500/30' :
                  'bg-green-900/30 border border-green-500/30'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-sm font-bold ${
                      index === 0 ? 'text-purple-400' :
                      index === 1 ? 'text-indigo-400' :
                      'text-green-400'
                    }`}>
                      {strategyContent.strategy_number}
                    </span>
                  </div>
                  <h3 className="text-white font-bold mb-1">{strategyContent.name}</h3>
                  <p className="text-gray-400 text-sm">{strategyContent.what?.substring(0, 100)}...</p>
                </div>
              );
            })}
          </div>
          
          <p className="text-gray-400 text-sm text-center">{content.task_note}</p>
          
          <NavigationButtons />
        </div>
      </motion.div>
    );
  };

  // Screen 30 - Final / Badge
  const renderScreen30 = () => {
    const content = t('understandingExpenses.screen30', { returnObjects: true });
    
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="px-4 md:px-8 py-6">
        <div className="max-w-lg mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-yellow-500 to-amber-600 mb-6">
            <Award className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-xl md:text-2xl font-bold text-white mb-4">{content.headline}</h1>
          <p className="text-gray-300 leading-relaxed mb-4">{content.body}</p>
          <p className="text-gray-400 text-sm mb-6">{content.tasks_note}</p>
          
          <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4 mb-6">
            <p className="text-purple-400 font-bold mb-2">{content.next_headline}</p>
            <p className="text-gray-300 text-sm">{content.next_body}</p>
          </div>
          
          <div className="bg-gradient-to-r from-yellow-900/30 to-amber-900/30 border border-yellow-500/30 rounded-xl p-4 mb-8">
            <p className="text-yellow-300 font-bold">{content.badge}</p>
          </div>
          
          {tasksAdded ? (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="flex items-center justify-center gap-2 text-green-400 mb-4"
            >
              <Check className="w-6 h-6" />
              <span className="text-lg font-medium">
                {lang === 'es' ? '¡Tareas agregadas!' : 'Tasks added!'}
              </span>
            </motion.div>
          ) : (
            <div className="space-y-3 mt-8">
              <button
                onClick={handleAddTasks}
                disabled={isAddingTasks}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg hover:from-green-400 hover:to-emerald-500 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="add-tasks-btn"
              >
                {isAddingTasks ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Plus className="w-5 h-5" />
                )}
                {content.addButton || (lang === 'es' ? 'Agregar a mi lista' : 'Add to my to-do list')}
              </button>
              <button
                onClick={handleSkip}
                className="w-full px-6 py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-all"
                data-testid="go-dashboard-btn"
              >
                {content.dashboardButton || (lang === 'es' ? 'Ir al inicio' : 'Go to dashboard')}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  // ============================================================================
  // MAIN RENDER LOGIC
  // ============================================================================
  
  const renderCurrentScreen = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
        </div>
      );
    }
    
    if (showContinuePrompt) {
      return renderContinuePrompt();
    }
    
    // Show paywall for Free users trying to access Premium content (Fix 1)
    if (showPaywall) {
      return renderPaywall();
    }
    
    // Screen routing
    switch (currentScreen) {
      case 1: return renderScreen1();
      case 2: return renderScreen2();
      case 3:
      case 4:
      case 5:
      case 6:
      case 7:
      case 8: return renderQuizQuestion();
      case 9: return renderScreen9();
      case 10: return renderScreen10();
      case 11: return renderPaywall(); // Screen 11 is the paywall for Free users (Premium users skip this)
      case 12: return renderContentScreen(12);
      case 13: return renderContentScreen(13);
      case 14: return renderContentScreen(14);
      case 15: return renderBehaviorDeepDive('comfort');
      case 16: return renderBehaviorDeepDive('status');
      case 17: return renderBehaviorDeepDive('impulse');
      case 18: return renderBehaviorDeepDive('rationalizer');
      case 19: return renderBehaviorDeepDive('escape');
      case 20: return renderBehaviorDeepDive('hunter');
      case 21: return renderScreen21();
      case 22: return renderContentScreen(22);
      case 23: return renderStrategyScreen(1);
      case 24: return renderStrategyScreen(2);
      case 25: return renderStrategyScreen(3);
      case 26: return renderStrategyScreen(4);
      case 27: return renderStrategyScreen(5);
      case 28: return renderStrategyScreen(6);
      case 29: return renderScreen29();
      case 30: return renderScreen30();
      default: return renderScreen1();
    }
  };
  
  // Progress percentage
  const progressPercent = Math.min((currentScreen / TOTAL_SCREENS) * 100, 100);
  
  return (
    <div className="fixed inset-0 bg-gray-900 z-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="w-6 h-6 text-purple-400" />
          <span className="text-white font-medium">{t('understandingExpenses.title')}</span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-all text-xl"
          data-testid="close-module-btn"
        >
          &times;
        </button>
      </div>
      
      {/* Progress bar */}
      <div className="h-1 bg-gray-800 flex-shrink-0">
        <div 
          className="h-full bg-gradient-to-r from-purple-600 to-indigo-600 transition-all duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      
      {/* Content - scrollable */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          {renderCurrentScreen()}
        </AnimatePresence>
      </div>
      
      {/* Saving indicator */}
      {isSaving && (
        <div className="absolute bottom-4 right-4 bg-gray-800 text-gray-300 px-3 py-1 rounded-full text-sm flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          {lang === 'es' ? 'Guardando...' : 'Saving...'}
        </div>
      )}
    </div>
  );
};

export default UnderstandingExpensesModule;
